import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { getErrorCodeForUi } from '@/api/errors';
import { Button } from '@/components/ui/button';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import { Skeleton } from '@/components/ui/skeleton';

import { useFhirMappedFields, useUpdateFhirMappedFields } from '../api';

export function MappedFieldsSection({
  integrationGuid,
}: {
  readonly integrationGuid: string;
}): ReactElement {
  const { t } = useTranslation();
  const fieldsQuery = useFhirMappedFields(integrationGuid);
  const save = useUpdateFhirMappedFields(integrationGuid);

  const available = fieldsQuery.data?.available ?? [];
  const selectedFromServer = fieldsQuery.data?.selected ?? [];

  const [selected, setSelected] = useState<readonly string[]>([]);

  // Seed local selection from the query, re-seeding only when the catalog
  // identity changes (not on every background refetch) so unsaved toggles
  // survive a refetch.
  const seedKey = `${available.join('|')}::${selectedFromServer.join('|')}`;
  const seededKey = useRef<string | null>(null);
  useEffect(() => {
    if (!fieldsQuery.isSuccess) return;
    if (seededKey.current === seedKey) return;
    seededKey.current = seedKey;
    setSelected(selectedFromServer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldsQuery.isSuccess, seedKey]);

  function toggle(field: string): void {
    save.reset();
    setSelected((prev) =>
      prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field],
    );
  }

  function selectAll(): void {
    save.reset();
    setSelected(available);
  }

  function deselectAll(): void {
    save.reset();
    setSelected([]);
  }

  const allSelected = available.length > 0 && available.every((f) => selected.includes(f));
  const noneSelected = selected.length === 0;

  function handleSave(): void {
    // Preserve catalog order in the payload for stable diffs.
    const ordered = available.filter((f) => selected.includes(f));
    save.mutate(ordered);
  }

  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-sm font-semibold">
          {t('routes.admin.fhirIntegrations.mappedFields.heading')}
        </h2>
        <p className="text-xs text-muted-foreground">
          {t('routes.admin.fhirIntegrations.mappedFields.description')}
        </p>
      </div>

      {fieldsQuery.isPending && (
        <div className="space-y-2" aria-hidden="true">
          {Array.from({ length: 2 }).map((_, i) => (
            <Skeleton key={i} className="h-8 w-full rounded-md" />
          ))}
        </div>
      )}

      {fieldsQuery.isError && (
        <div className="max-w-xl">
          <FetchErrorCallout
            error={fieldsQuery.error}
            heading={t('routes.admin.fhirIntegrations.mappedFields.error.heading')}
            message={t('routes.admin.fhirIntegrations.mappedFields.error.message')}
            codeLabel={t('routes.admin.fhirIntegrations.mappedFields.error.codeLabel')}
            onRetry={() => {
              fieldsQuery.refetch().catch(() => undefined);
            }}
            retryLabel={t('routes.admin.fhirIntegrations.mappedFields.error.retry')}
          />
        </div>
      )}

      {fieldsQuery.isSuccess && available.length === 0 && (
        <p className="text-sm text-muted-foreground" role="status">
          {t('routes.admin.fhirIntegrations.mappedFields.empty')}
        </p>
      )}

      {fieldsQuery.isSuccess && available.length > 0 && (
        <>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={selectAll}
              disabled={allSelected}
            >
              {t('routes.admin.fhirIntegrations.mappedFields.selectAll')}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={deselectAll}
              disabled={noneSelected}
            >
              {t('routes.admin.fhirIntegrations.mappedFields.deselectAll')}
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
            {available.map((field) => (
              <label key={field} className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={selected.includes(field)}
                  onChange={() => {
                    toggle(field);
                  }}
                  className="h-3.5 w-3.5"
                />
                {field}
              </label>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <Button type="button" size="sm" loading={save.isPending} onClick={handleSave}>
              {t('routes.admin.fhirIntegrations.mappedFields.save')}
            </Button>
            {save.isSuccess && (
              <span className="text-xs text-muted-foreground" role="status">
                {t('routes.admin.fhirIntegrations.mappedFields.saved')}
              </span>
            )}
            {save.isError && (
              <span className="text-xs text-red-600" role="alert">
                {t('routes.admin.fhirIntegrations.mappedFields.saveError', {
                  code: getErrorCodeForUi(save.error),
                })}
              </span>
            )}
          </div>
        </>
      )}
    </section>
  );
}
